import {
  init,
  thirdPartyErrorFilterIntegration,
  browserTracingIntegration,
  replayIntegration,
} from '@sentry/browser'
import { config, runtimeConfig } from './config'

/**
 * Disable global error handler.
 * Errors will be captured manually.
 * @see https://docs.sentry.io/platforms/javascript/configuration/integrations/globalhandlers/
 */
const disabledDefaultIntegrations = ['GlobalHandlers', 'BrowserApiErrors']

// reminder: also change sentrySw.ts (except replayIntegration, which is not supported in service workers)
init({
  enabled: config.isProd,
  dsn: config.sentryDsn,
  release: config.commitHash,
  sendDefaultPii: true,
  initialScope: {
    tags: {
      ssr: runtimeConfig.isSSR,
    },
  },
  integrations: function (integrations) {
    const filtered = integrations.filter(
      i => !disabledDefaultIntegrations.includes(i.name),
    )
    return [
      ...filtered,
      thirdPartyErrorFilterIntegration({
        // Specified in vite.config.mts
        filterKeys: ['pixai-web-app-key'],
        /**
         * NOTE: "exclusively-contains-third-party" is needed to report errors in apollo client
         * Change it back to default behavior after we figured out why
         */
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
      browserTracingIntegration(),
      replayIntegration({
        // Mask all text content and block all media by default for privacy
        maskAllText: true,
        blockAllMedia: true,
      }),
    ]
  },
  // Copied and modified from https://gist.github.com/impressiver/5092952
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
    // Dialog canceled
    'user cancelled',
    // Auth Errors
    'must be logged in to access this resource',
    'You must be logged in to perform this action: me',
    'Non-Error exception captured with keys',
    // Firebase messaging
    'FirebaseError',
    'required to use the Firebase SDK',
    // Network/Fetch Error
    'Network Error',
    'NetworkError',
    'Failed to fetch',
    'Request timeout',
    'AxiosError',
    'Load failed',
    'Task listing rate limit exceeded',
    'Unexpected EOF',
    // Currently unresolvable errors
    'Unable to preload CSS',
    'Importing a module script failed',
    'Event `Event` (type=error) captured as exception',
    'error loading dynamically imported module',
    'Failed to register a ServiceWorker',
    '.subscribePersonalEvents(...).pipe is not a function',
    "Failed to execute 'removeChild' on 'Node'",
    // Ignore errors from unsupported browsers
    'findLastIndex is not a function',
    'findLast is not a function',
  ],
  denyUrls: [
    // Google Adsense
    /pagead\/js/i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
  // tracesSampleRate: 0.0005,
  sampleRate: 1,
  tracesSampleRate: 0.01,
  // Session Replay sample rates
  replaysSessionSampleRate: 0.0001,
  replaysOnErrorSampleRate: 1.0,
})
